import React, { ForwardedRef, forwardRef } from 'react';

import { ButtonTypes, StandardButton } from '@eika/button';
import { ErrorCard } from '@eika/card';
import { Modal, ModalDialogProps } from '@eika/modal';

import CountdownDisplay from './countdown/Countdown';
import fallbackDictionary from './fallbackDictionary';
import CenteredLoader from './loader/Loader';
import { SiteCoreDictionary } from './types';

import './StateHandlingModal.scss';

export interface StateHandlingModalProps extends ModalDialogProps {
    /** To display a loading state */
    loading?: boolean;
    /** Text displayed below loading indicator */
    loadingText?: string;
    /** Display error description if something went wrong */
    error?: string | JSX.Element | undefined;
    /** Countdown time in milliseconds */
    countdown?: number;
    /** Flag for centering content horizontally in modal */
    centerContentHorizontally?: boolean;
    /** Flag for centering content vertically in modal */
    centerContentVertically?: boolean;
    /** Function for allowing user to try again */
    tryAgainClick?: () => void;
    /** Sitecore dictionary */
    sitecoreDictionary?: SiteCoreDictionary;
    /** To show inverted button colors */
    inverted?: boolean;
    /** To have custom text on back button */
    backButtonText?: string;
    /** Custom button if needed */
    customBackButton?: React.JSX.Element;
}

interface StateHandlingBoundaryProps {
    loading: boolean;
    loadingText?: string;
    error: string | JSX.Element | undefined;
    tryAgainClick?: () => void;
    dictionary: SiteCoreDictionary;
    children: React.ReactNode;
    inverted?: boolean;
}

const StateHandlingBoundary = ({
    loading,
    loadingText,
    error,
    tryAgainClick,
    dictionary,
    children,
    inverted = false,
}: StateHandlingBoundaryProps) => {
    const { genericTryAgain, genericErrorHeader } = dictionary;

    if (loading) {
        return <CenteredLoader text={loadingText} />;
    }

    if (typeof error !== 'undefined') {
        if (typeof error === 'string') {
            return (
                <div className="error-card-container">
                    <ErrorCard title={genericErrorHeader} description={error}>
                        {tryAgainClick && (
                            <StandardButton
                                buttonType={inverted ? ButtonTypes.STANDARD_NEGATIVE : ButtonTypes.STANDARD_POSITIVE}
                                onClick={() => tryAgainClick()}
                            >
                                {genericTryAgain}
                            </StandardButton>
                        )}
                    </ErrorCard>
                </div>
            );
        }

        return <>{error}</>;
    }

    return <>{children}</>;
};

const StateHandlingModal = forwardRef(
    (
        {
            loading = false,
            loadingText,
            error,
            sitecoreDictionary,
            countdown,
            tryAgainClick,
            centerContentHorizontally = false,
            centerContentVertically = false,
            children,
            inverted,
            backButtonText,
            customBackButton,
            ...rest
        }: StateHandlingModalProps,
        ref?: ForwardedRef<HTMLDivElement>,
    ) => {
        const dictionary: SiteCoreDictionary = { ...fallbackDictionary, ...sitecoreDictionary };
        const { formClose } = dictionary;

        const alignStyling = `${centerContentHorizontally ? 'centered-horizontally' : ''} ${centerContentVertically ? 'centered-vertically' : ''}`;

        return (
            <Modal {...rest} ref={ref}>
                <div className={`content-with-footer ${alignStyling}`}>
                    <div className="main-content">
                        <StateHandlingBoundary
                            loading={loading}
                            loadingText={loadingText}
                            error={error}
                            dictionary={dictionary}
                            tryAgainClick={tryAgainClick}
                        >
                            {children}
                        </StateHandlingBoundary>
                    </div>
                    <div className="footer-container">
                        <CountdownDisplay
                            countdown={countdown}
                            onClose={() => rest.onClose && rest.onClose()}
                            dictionary={dictionary}
                        />
                        {customBackButton ?? (
                            <StandardButton
                                buttonType={inverted ? ButtonTypes.STANDARD_NEGATIVE : ButtonTypes.STANDARD_POSITIVE}
                                onClick={() => rest.onClose && rest.onClose()}
                            >
                                {backButtonText ?? formClose}
                            </StandardButton>
                        )}
                    </div>
                </div>
            </Modal>
        );
    },
);

export default StateHandlingModal;
